import React, { useState, useEffect } from 'react';
import apiService from '../services/api';

interface Purchase {
  id: string;
  sessionId: string;
  productName: string;
  productSize: string;
  amountTotal: number;
  currency: string;
  customerEmail: string;
  customerName: string;
  purchasedAt: string;
  orderStatus: string;
  shippingDate?: string;
  trackingNumber?: string;
  shippingAddress?: {
    name: string;
    line1: string;
    line2?: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
  };
}

interface ShippingRate {
  objectId: string;
  serviceLevel: string;
  serviceLevelName: string;
  carrier: string;
  amount: string;
  currency: string;
  estimatedDays?: number;
}

interface ShippingStatus {
  configured: boolean;
  testMode: boolean;
  apiKeyPresent: boolean;
}

const ShippingManagement: React.FC = () => {
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [selectedPurchase, setSelectedPurchase] = useState<Purchase | null>(null);
  const [shippingRates, setShippingRates] = useState<ShippingRate[]>([]);
  const [selectedRate, setSelectedRate] = useState<string>('');
  const [shippingStatus, setShippingStatus] = useState<ShippingStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    fetchPurchases();
    fetchShippingStatus();
  }, []);

  const fetchPurchases = async () => {
    try {
      const response = await apiService.getPurchases();
      if (response.success) {
        setPurchases(response.data.purchases);
      }
    } catch (err) {
      console.error('Error fetching purchases:', err);
      setError('Failed to fetch purchases');
    } finally {
      setLoading(false);
    }
  };

  const fetchShippingStatus = async () => {
    try {
      const response = await apiService.getShippingStatus();
      if (response.success) {
        setShippingStatus(response.data);
      }
    } catch (err) {
      console.error('Error fetching shipping status:', err);
    }
  };

  const fetchShippingRates = async (purchaseId: string) => {
    try {
      setProcessing(true);
      const response = await apiService.getShippingRates(purchaseId);
      if (response.success) {
        setShippingRates(response.data.rates);
        setSelectedPurchase(response.data);
      } else {
        setError(response.message || 'Failed to fetch shipping rates');
      }
    } catch (err) {
      console.error('Error fetching shipping rates:', err);
      setError('Failed to fetch shipping rates');
    } finally {
      setProcessing(false);
    }
  };

  const createShippingLabel = async () => {
    if (!selectedPurchase || !selectedRate) {
      setError('Please select a purchase and shipping rate');
      return;
    }

    try {
      setProcessing(true);
      const response = await apiService.createShippingLabel(selectedPurchase.id, selectedRate);

      if (response.success) {
        setSuccess(`Shipping label created successfully! Tracking number: ${response.data.trackingNumber}`);
        await fetchPurchases();
        setSelectedPurchase(null);
        setShippingRates([]);
        setSelectedRate('');
      } else {
        setError(response.message || 'Failed to create shipping label');
      }
    } catch (err) {
      console.error('Error creating shipping label:', err);
      setError('Failed to create shipping label');
    } finally {
      setProcessing(false);
    }
  };

  const formatPrice = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'text-orange-700 bg-orange-100';
      case 'confirmed': return 'text-brand-blue bg-brand-blue/10';
      case 'shipped': return 'text-scarf-green bg-scarf-green/10';
      case 'delivered': return 'text-scarf-green bg-scarf-green/20';
      case 'canceled': return 'text-brand-red bg-brand-red/10';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-blue"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-title font-bold text-deep-navy">Shipping Management</h2>
        <div className="flex items-center space-x-4">
          {shippingStatus && (
            <div className={`px-3 py-1 rounded-full text-sm font-bold ${
              shippingStatus.configured
                ? 'bg-scarf-green/10 text-scarf-green'
                : 'bg-brand-red/10 text-brand-red'
            }`}>
              {shippingStatus.configured ? 'Shippo Connected' : 'Shippo Not Configured'}
            </div>
          )}
        </div>
      </div>

      {/* Status Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="flex">
            <div className="text-brand-red">Warning</div>
            <div className="ml-3">
              <p className="text-sm text-brand-red">{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-brand-red hover:text-brand-red/70"
            >
              x
            </button>
          </div>
        </div>
      )}

      {success && (
        <div className="bg-scarf-green/10 border border-scarf-green/30 rounded-xl p-4">
          <div className="flex">
            <div className="text-scarf-green">Success</div>
            <div className="ml-3">
              <p className="text-sm text-scarf-green">{success}</p>
            </div>
            <button
              onClick={() => setSuccess(null)}
              className="ml-auto text-scarf-green hover:text-scarf-green/70"
            >
              x
            </button>
          </div>
        </div>
      )}

      {/* Configuration Warning */}
      {shippingStatus && !shippingStatus.configured && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
          <div className="flex">
            <div className="text-orange-600">Warning</div>
            <div className="ml-3">
              <p className="text-sm text-orange-700">
                Shippo is not configured. Please set your SHIPPO_API_KEY in the environment variables.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Purchases List */}
      <div className="bg-white rounded-2xl border border-ice-teal-light shadow-sm">
        <div className="px-6 py-4 border-b border-ice-teal-light">
          <h3 className="text-lg font-title font-bold text-deep-navy">Recent Purchases</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100">
            <thead className="bg-frost-white">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-bold text-deep-navy uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-bold text-deep-navy uppercase tracking-wider">
                  Product
                </th>
                <th className="px-6 py-3 text-left text-xs font-bold text-deep-navy uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-bold text-deep-navy uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-bold text-deep-navy uppercase tracking-wider">
                  Tracking
                </th>
                <th className="px-6 py-3 text-left text-xs font-bold text-deep-navy uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {purchases.map((purchase) => (
                <tr key={purchase.id} className="hover:bg-frost-white">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-bold text-deep-navy">
                        {purchase.customerName || 'N/A'}
                      </div>
                      <div className="text-sm text-gray-500">{purchase.customerEmail}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-800">{purchase.productName}</div>
                    <div className="text-sm text-gray-500">{purchase.productSize}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-800">
                    {formatPrice(purchase.amountTotal, purchase.currency)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-bold rounded-full ${getStatusColor(purchase.orderStatus)}`}>
                      {purchase.orderStatus}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {purchase.trackingNumber || 'Not shipped'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    {purchase.orderStatus === 'confirmed' && purchase.shippingAddress && (
                      <button
                        onClick={() => fetchShippingRates(purchase.id)}
                        disabled={processing}
                        className="text-brand-blue hover:text-brand-blue-dark disabled:opacity-50"
                      >
                        {processing ? 'Loading...' : 'Ship Now'}
                      </button>
                    )}
                    {purchase.trackingNumber && (
                      <a
                        href={`/shipping/track/${purchase.trackingNumber}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-scarf-green hover:text-scarf-green/80 ml-2"
                      >
                        Track
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Shipping Rates Modal */}
      {selectedPurchase && shippingRates.length > 0 && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-8 border border-ice-teal-light w-11/12 md:w-3/4 lg:w-1/2 shadow-xl rounded-3xl bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-title font-bold text-deep-navy mb-4">
                Select Shipping Rate for {selectedPurchase.customerName}
              </h3>

              <div className="space-y-3 mb-6">
                {shippingRates.map((rate) => (
                  // The visible price and delivery estimate are laid out in
                  // columns, which reads as fragments out of context. aria-label
                  // names the option as one sentence instead.
                  <label
                    key={rate.objectId}
                    htmlFor={`shipping-rate-${rate.objectId}`}
                    aria-label={`${rate.carrier} ${rate.serviceLevelName}, ${formatPrice(Number.parseFloat(rate.amount) * 100, rate.currency)}, ${rate.estimatedDays ? `${rate.estimatedDays} days` : 'delivery time varies'}`}
                    className="flex items-center p-3 border border-ice-teal-light rounded-xl hover:bg-frost-white cursor-pointer"
                  >
                    <input
                      id={`shipping-rate-${rate.objectId}`}
                      type="radio"
                      name="shippingRate"
                      value={rate.objectId}
                      checked={selectedRate === rate.objectId}
                      onChange={(e) => setSelectedRate(e.target.value)}
                      className="mr-3"
                    />
                    <div className="flex-1">
                      <div className="flex justify-between items-center">
                        <span className="font-bold text-deep-navy">{rate.serviceLevelName}</span>
                        <span className="text-lg font-bold text-deep-navy">
                          {formatPrice(Number.parseFloat(rate.amount) * 100, rate.currency)}
                        </span>
                      </div>
                      <div className="text-sm text-gray-500">
                        {rate.carrier} {rate.estimatedDays ? `${rate.estimatedDays} days` : 'Delivery time varies'}
                      </div>
                    </div>
                  </label>
                ))}
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setSelectedPurchase(null);
                    setShippingRates([]);
                    setSelectedRate('');
                  }}
                  className="px-4 py-2 text-sm font-bold text-gray-700 bg-frost-white border border-ice-teal-light rounded-xl hover:bg-ice-pale"
                >
                  Cancel
                </button>
                <button
                  onClick={createShippingLabel}
                  disabled={!selectedRate || processing}
                  className="px-4 py-2 text-sm font-bold text-white bg-brand-blue border border-transparent rounded-xl hover:bg-brand-blue-dark disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {processing ? 'Creating Label...' : 'Create Shipping Label'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShippingManagement;
